import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, Sparkles } from 'lucide-react';
import {
  AiModelPicker, AppButton, FormField, Input, NativeSelect, Textarea, cn,
} from '../ui';
import useAiModels from '../../hooks/useAiModels';
import AiGenerateModal from './AiGenerateModal';
import PageContentField, { TEXTUAL_FIELD_TYPES, isWideField } from './PageContentField';
import PageSectionCard from './PageSectionCard';
import { aiGeneratePage, aiGenerateSeo } from '../../utils/cmsAdminApi';
import { countFilledFields, isSectionShown } from './cmsSiteModel';

// ─── PagesTab — the copy and images of each website page ─────────────────
//
// A list of the active layout's pages on the left, the chosen page's
// sections on the right. Each section is a collapsible card showing how much
// of it the admin has filled and, where the website allows, a show/hide
// checkbox. Every edit goes into the shared draft; the page's save bar saves.
//
// `focus` ({ page, section, nonce }) is a request from elsewhere — the setup
// checklist — to open a page at a section.

const sectionKey = (section) => section.id || section.title;

// A section or a field may declare `visibleWhen` to appear only while another
// field on the page is on (theme-3's job-application copy: `{ key, equals:
// true }`) or holds a chosen value (the About page's one-or-two team sections:
// `{ key, equals: 'separate' }` / `{ key, notEquals: 'separate' }`).
const meetsCondition = (item, pageContent) => {
  const cond = item.visibleWhen;
  if (!cond) return true;
  const raw = pageContent?.[cond.key];
  if ('notEquals' in cond) return (raw ?? '') !== cond.notEquals;
  if (typeof cond.equals === 'string') return (raw ?? '') === cond.equals;
  const val = raw === undefined || raw === null || raw === '' ? false : raw !== false && raw !== 'false';
  return val === cond.equals;
};

const textualFields = (section) => section.fields.filter((f) => TEXTUAL_FIELD_TYPES.has(f.type));

/**
 * Comma-separated keywords, kept as typed until the field is left. The rest
 * of the props are FormField's id / aria wiring, passed to the input.
 */
function KeywordsInput({ value, onChange, ...props }) {
  const joined = (Array.isArray(value) ? value : []).join(', ');
  const [draft, setDraft] = useState(joined);
  // Re-sync when the value changes from outside (AI draft, discard, page switch).
  useEffect(() => { setDraft(joined); }, [joined]);
  const parse = (s) => s.split(',').map((k) => k.trim()).filter(Boolean);
  return (
    <Input
      {...props}
      placeholder="java training, full stack course, hyderabad"
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        onChange(parse(e.target.value));
      }}
      onBlur={() => setDraft(parse(draft).join(', '))}
    />
  );
}

export default function PagesTab({
  schema, content, setPageField, courses, aiEnabled, notify, focus, pageUrl, layoutName,
}) {
  const [activeKey, setActiveKey] = useState(schema[0]?.pageKey);
  const [expanded, setExpanded] = useState({});
  const [aiPage, setAiPage] = useState(null);
  const [seoBusy, setSeoBusy] = useState(false);
  const sectionRefs = useRef({});

  // The schema is the active layout's; after a layout switch the old page key
  // may not exist, so fall back to the first page rather than render nothing.
  const page = schema.find((p) => p.pageKey === activeKey) || schema[0];
  const pageContent = content?.[page.pageKey] || {};
  const seoKey = `${page.pageKey}:seo`;
  const seoOpen = !!expanded[seoKey];
  // The catalogue is fetched only once the SEO section is open.
  const seoAi = useAiModels('/cms/ai/models', !!aiEnabled && seoOpen);

  const isOpen = (key, index) => expanded[`${page.pageKey}:${key}`] ?? index === 0;
  const toggle = (key, index) => setExpanded((prev) => ({
    ...prev,
    [`${page.pageKey}:${key}`]: !(prev[`${page.pageKey}:${key}`] ?? index === 0),
  }));

  useEffect(() => {
    if (!focus?.page) return;
    setActiveKey(focus.page);
    if (focus.section) setExpanded((prev) => ({ ...prev, [`${focus.page}:${focus.section}`]: true }));
  }, [focus]);

  useEffect(() => {
    if (!focus?.section || focus.page !== page.pageKey) return;
    sectionRefs.current[`${focus.page}:${focus.section}`]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focus, page.pageKey]);

  const pageStats = useMemo(() => schema.map((p) => {
    const fields = p.sections.flatMap((s) => s.fields).filter((f) => f.type !== 'toggle');
    return { key: p.pageKey, filled: countFilledFields(fields, content?.[p.pageKey]), total: fields.length };
  }), [schema, content]);

  const setField = (key, value) => setPageField(page.pageKey, key, value);
  const setSeo = (key, value) => setField('seo', { ...(pageContent.seo || {}), [key]: value });

  // ── AI: page copy ─────────────────────────────────────────────────────
  // Only plain-text fields are offered: a list, image or link written back
  // as prose would replace a structured value and break the page reading it.
  const buildSectionsPayload = (p) => p.sections
    .map((s) => ({ title: s.title, fields: textualFields(s).map((f) => ({ key: f.key, label: f.label, type: f.type })) }))
    .filter((s) => s.fields.length > 0);

  const applyAiResult = (p) => (result) => {
    const fields = result?.fields || {};
    let applied = 0;
    p.sections.forEach((section) => {
      textualFields(section).forEach((f) => {
        const v = fields[`${section.title}__${f.key}`];
        if (v != null && String(v).trim()) {
          setPageField(p.pageKey, f.key, String(v));
          applied += 1;
        }
      });
    });
    notify(`AI filled ${applied} field${applied === 1 ? '' : 's'} — review them, then save.`);
  };

  const runAi = (p) => ({ prompt, tone, ...ai }, { regenerate }) => aiGeneratePage({
    pageKey: p.pageKey,
    pageLabel: p.label,
    sections: buildSectionsPayload(p),
    prompt,
    tone,
    regenerate,
    ...ai,
  });

  // ── AI: search listing ────────────────────────────────────────────────
  const draftSeo = async () => {
    setSeoBusy(true);
    try {
      const context = Object.entries(pageContent)
        .filter(([k, v]) => k !== 'seo' && typeof v === 'string' && v.trim())
        .slice(0, 12)
        .map(([k, v]) => `${k}: ${String(v).slice(0, 200)}`)
        .join('\n');
      const r = await aiGenerateSeo({
        pageType: page.pageKey,
        context,
        regenerate: !!pageContent.seo?.metaTitle,
        ...seoAi.payload,
      });
      const d = r?.data || {};
      const cur = pageContent.seo || {};
      setField('seo', {
        ...cur,
        metaTitle: d.metaTitle || cur.metaTitle || '',
        metaDescription: d.metaDescription || cur.metaDescription || '',
        metaKeywords: Array.isArray(d.keywords) && d.keywords.length ? d.keywords : (cur.metaKeywords || []),
      });
      const model = r?.usage?.model;
      notify(`Search listing drafted${model ? ` by ${model}` : ''} — review it, then save.`);
    } catch (e) {
      notify(e?.response?.data?.message || 'The AI could not draft the search listing.', 'error');
    } finally {
      setSeoBusy(false);
    }
  };

  const visibleSections = page.sections.filter((s) => meetsCondition(s, pageContent));

  return (
    <div className="tw-grid tw-grid-cols-1 tw-items-start tw-gap-5 min-[992px]:tw-grid-cols-[220px_minmax(0,1fr)]">
      {/* Page list — a select below 992px, where a side rail would crowd the form. */}
      <div className="min-[992px]:tw-hidden">
        <FormField label="Page">
          <NativeSelect value={page.pageKey} onChange={(e) => setActiveKey(e.target.value)}>
            {schema.map((p) => <option key={p.pageKey} value={p.pageKey}>{p.label}</option>)}
          </NativeSelect>
        </FormField>
      </div>
      <nav aria-label="Website pages" className="tw-hidden min-[992px]:tw-sticky min-[992px]:tw-top-4 min-[992px]:tw-block">
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0">
          {schema.map((p, i) => {
            const Icon = p.icon;
            const active = p.pageKey === page.pageKey;
            const { filled, total } = pageStats[i];
            return (
              <li key={p.pageKey}>
                <button
                  type="button"
                  aria-current={active ? 'page' : undefined}
                  onClick={() => setActiveKey(p.pageKey)}
                  className={cn(
                    'tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded-sm tw-border-0 tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-focus-ring',
                    active
                      ? 'tw-bg-primary-soft tw-font-semibold tw-text-primary'
                      : 'tw-bg-transparent tw-text-content-muted hover:tw-bg-surface-hover hover:tw-text-content'
                  )}
                >
                  {Icon && <Icon size={16} aria-hidden="true" />}
                  <span className="tw-min-w-0 tw-flex-1 tw-truncate">{p.label}</span>
                  {total > 0 && (
                    <>
                      <span className="tw-text-xs tw-tabular-nums tw-text-content-subtle" aria-hidden="true">{filled}/{total}</span>
                      <span className="tw-sr-only">, {filled} of {total} fields filled</span>
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4">
        <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <h2 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-content">{page.label}</h2>
            <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-content-muted">
              {page.intro || 'Blank fields show the sample text shipped with the site.'}
              {' '}These fields belong to your {layoutName} layout.
            </p>
          </div>
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {aiEnabled && (
              <AppButton size="sm" variant="outline" onClick={() => setAiPage(page)}>
                <Sparkles size={16} /> Draft with AI
              </AppButton>
            )}
            {page.path && (
              <AppButton as="a" size="sm" variant="outline" href={pageUrl(page.path)} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={16} /> View page
              </AppButton>
            )}
          </div>
        </div>

        {visibleSections.map((section, i) => {
          const key = sectionKey(section);
          const vis = section.visibility;
          const fields = section.fields.filter((f) => meetsCondition(f, pageContent));
          return (
            <PageSectionCard
              key={key}
              ref={(el) => { sectionRefs.current[`${page.pageKey}:${key}`] = el; }}
              title={section.title}
              hint={section.hint}
              filled={countFilledFields(fields, pageContent)}
              total={fields.filter((f) => f.type !== 'toggle').length}
              expanded={isOpen(key, i)}
              onToggle={() => toggle(key, i)}
              visibility={vis ? {
                shown: isSectionShown(pageContent, vis.key, vis),
                onChange: (shown) => setField(vis.key, shown),
                hiddenNote: vis.hiddenNote,
              } : undefined}
            >
              <div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-4 min-[721px]:tw-grid-cols-2">
                {fields.map((f) => (
                  <div key={f.key} className={cn('tw-min-w-0', isWideField(f) && 'tw-col-span-full')}>
                    <PageContentField
                      field={f}
                      value={pageContent[f.key]}
                      onChange={(v) => setField(f.key, v)}
                      onShowToast={notify}
                      courses={courses}
                    />
                  </div>
                ))}
              </div>
            </PageSectionCard>
          );
        })}

        <PageSectionCard
          title="Search engine listing"
          hint="How this page appears in Google and when shared. Blank fields use your site-wide SEO defaults."
          filled={['metaTitle', 'metaDescription', 'metaKeywords', 'ogImage']
            .filter((k) => (Array.isArray(pageContent.seo?.[k]) ? pageContent.seo[k].length : String(pageContent.seo?.[k] || '').trim()))
            .length}
          total={4}
          expanded={seoOpen}
          onToggle={() => setExpanded((prev) => ({ ...prev, [seoKey]: !prev[seoKey] }))}
          actions={aiEnabled && seoOpen ? (
            <AppButton size="sm" variant="outline" onClick={draftSeo} disabled={seoBusy}>
              <Sparkles size={16} /> {seoBusy ? 'Drafting…' : 'Draft with AI'}
            </AppButton>
          ) : null}
        >
          {aiEnabled && <AiModelPicker {...seoAi} />}
          <div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-4 min-[721px]:tw-grid-cols-2">
            <FormField label="Title" hint={`${String(pageContent.seo?.metaTitle || '').length}/60 characters is ideal.`}>
              <Input
                maxLength={70}
                placeholder={`e.g. ${page.label} — Your Institute`}
                value={pageContent.seo?.metaTitle || ''}
                onChange={(e) => setSeo('metaTitle', e.target.value)}
              />
            </FormField>
            <FormField label="Keywords" hint="Separate with commas.">
              <KeywordsInput value={pageContent.seo?.metaKeywords} onChange={(v) => setSeo('metaKeywords', v)} />
            </FormField>
            <FormField
              className="tw-col-span-full"
              label="Description"
              hint={`${String(pageContent.seo?.metaDescription || '').length}/160 characters is ideal.`}
            >
              <Textarea
                rows={2}
                maxLength={200}
                placeholder="One or two sentences on what this page offers."
                value={pageContent.seo?.metaDescription || ''}
                onChange={(e) => setSeo('metaDescription', e.target.value)}
              />
            </FormField>
            <FormField className="tw-col-span-full" label="Share image link" hint="Shown when the page is shared on social media. 1200 × 630 px works best.">
              <Input
                type="url"
                placeholder="https://…"
                value={pageContent.seo?.ogImage || ''}
                onChange={(e) => setSeo('ogImage', e.target.value)}
              />
            </FormField>
          </div>
        </PageSectionCard>
      </div>

      {aiPage && (
        <AiGenerateModal
          show={!!aiPage}
          onHide={() => setAiPage(null)}
          title={`Draft the ${aiPage.label} page`}
          description="The AI drafts headings and text for each section of this page. Review what it writes before you save."
          onGenerate={runAi(aiPage)}
          onApply={applyAiResult(aiPage)}
          renderPreview={(result) => {
            const entries = Object.entries(result?.fields || {});
            if (!entries.length) return <em>No suggestions came back. Try again.</em>;
            return (
              <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0 tw-text-sm">
                {entries.map(([k, v]) => (
                  <li key={k}>
                    <strong>{k.split('__').slice(1).join('__') || k}:</strong> {v}
                  </li>
                ))}
              </ul>
            );
          }}
        />
      )}
    </div>
  );
}
